import { Rectangle } from 'pixi.js';
import { Config } from '../../video-data/config';
import { WordElementMetrics } from './WordElementMetrics';
import type { WordStyle } from './WordStyle';
import { WordToken } from './WordToken';

/**
 * The metric data structure for building a {@link Luminary.TextWord), which represents a visually
 * continuous word to the user. Normally words are split by spaces and new lines, but this
 * structure allows us to further split on render dependent boundaries like text color and font.
 *
 * A TextWord metric is further broken down into {@link Luminary.WordElement}, which each
 * represent a uniform (styling and font are consistent) text sprite that can be rendered.
 *
 */
export class TextWordMetrics {
    classType = 'TextWordMetrics';
    #wordElementList: WordElementMetrics[] = [];
    #isPositionSet = false;

    /**
     * The number of word elements part of this text word.
     */
    get numWordElements(): number {
        return this.#wordElementList.length;
    }

    /**
     * Retrieves the internal list of word element metrics.
     */
    get wordElements(): WordElementMetrics[] {
        return this.#wordElementList;
    }

    /**
     * The text in the text word as a single string.
     */
    get text(): string {
        return this.#wordElementList.reduce((acc, wordElement) => acc + wordElement.text, '');
    }

    /**
     * The advance width of the text word.
     */
    get advanceWidth(): number {
        return this.#wordElementList.reduce((acc, wordElement) => acc + wordElement.advanceWidth, 0);
    }

    /**
     * The horizontal distance from the origin pen position to the left bounding box
     * edge.
     */
    get bearingX(): number {
        if (this.numWordElements === 0) {
            return 0;
        }
        // metrics assume left aligned text
        return this.#wordElementList[0].bearingX;
    }

    /**
     * The average x-height of all word elements. It uses the average since different
     * fonts could be used throughout the elements.
     */
    get xHeight(): number {
        const sum = this.#wordElementList.reduce((acc, wordElement) => acc + wordElement.xHeight, 0);
        return sum / this.numWordElements;
    }


    /**
     * The vertical distance from the baseline to the highest outline marking of the text word.
     * It is a non-negative number.
     */
    get ascent(): number {
        return this.#wordElementList.reduce((max, wordElement) => Math.max(max, wordElement.ascent), 0);
    }

    /**
     * The vertical distance from the baseline to the lowest outline marking of the text word.
     * It is a negative number with a maximum of zero.
     */
    get descent(): number {
        return this.#wordElementList.reduce((min, wordElement) => Math.min(min, wordElement.descent), 0);
    }
    /**
     * The number of characters in the text word.
     */
    get numChars(): number {
        return this.#wordElementList.reduce((acc, wordElement) => acc + wordElement.numChars, 0);
    }

    /**
     * The x-position of the text word's bounding box.
     */
    get x(): number {
        return this.#wordElementList.reduce((min, wordElement) => Math.min(min, wordElement.x), Infinity);
    }

    /**
     * The y-position of the text word's bounding box.
     */
    get y(): number {
        return this.#wordElementList.reduce((min, wordElement) => Math.min(min, wordElement.y), Infinity);
    }

    /**
     * The style used in generating the metrics. Note it could either return
     * the fill or highlight style.
     */
    get style(): WordStyle {
        if (this.numWordElements === 0) {
            throw new Error('Cannot get text word style, no word elements exist');
        }
        return this.#wordElementList[0].style;
    }

    /**
     * Adds a word element metric to the internal data list.
     *
     * @param {Luminary.WordElementMetrics} wordElementMetrics
     */
    addElement(wordElementMetrics: WordElementMetrics) {
        if (!(wordElementMetrics instanceof WordElementMetrics)) {
            throw new Error('Only WordElementMetrics objects can be added to TextWordMetrics');
        }
        this.#wordElementList.push(wordElementMetrics);
    }

    /**
     * The bounding box which tightly fits the entire line.
     */
    getBounds(): Rectangle {
        if (this.numWordElements === 0) {
            return Rectangle.EMPTY;
        }
        const box = this.#wordElementList[0].getBounds();
        for (let i = 1; i < this.numWordElements; i++) {
            // only include if width is greater than zero (otherwise a zero sized box
            // positioned somewhere will result in the bounds to enlarge)
            const bounds = this.#wordElementList[i].getBounds();
            if (bounds.width > 0) {
                box.enlarge(bounds);
            }
        }
        return box;
    }

    /**
     * Set the position of the text word by positioning each of the underlying word elements.
     *
     * @param advanceOriginX - minimum x position of the text word (x-origin of the
     *                                  advance width)
     * @param yBaseline - the y position of the baseline for the text word.
     */
    setPosition(advanceOriginX = 0, yBaseline = 0) {
        let offsetX = 0;
        this.#wordElementList.forEach(wordElement => {
            // Reference x-pos + distance to the word element + distance from origin to bbox
            wordElement.x = advanceOriginX + offsetX + wordElement.bearingX;

            // The reference is the top left, so map the baseline to the top
            wordElement.y = yBaseline - wordElement.ascent;

            offsetX += wordElement.advanceWidth;
        });
        this.#isPositionSet = true;
    }


    /**
     * After the initial left-to-right and left aligned position has been set, this modifies the
     * positions to account for the desired reading direction and horizontal alignment.
     *
     * @param lineOffsetX - the offset into the current line for this text word
     */
    setAlignmentAndReadingDirection(lineOffsetX: number) {
        if (!this.#isPositionSet) {
            throw new Error(
                'Position needs to first be set before setting alignment and reading direction'
            );
        }

        this.#wordElementList.forEach(wordElement => {
            if (this.style.readingDirection === Config.READING_DIRECTION.LTR) {
                wordElement.x = lineOffsetX + wordElement.x;
            }
            else if (this.style.readingDirection === Config.READING_DIRECTION.RTL) {
                wordElement.x = lineOffsetX - wordElement.advanceWidth - wordElement.x;
            }
            else {
                console.warn('Unrecognized readingDirection', this.style.readingDirection);
            }
        });
    }

    /**
     * Returns a WordToken containing a portion of the text. The WordToken
     * can be used to generate TextWordMetrics.
     *
     * @param start - zero-based index at which to begin extraction
     * @param end - extracts up to but not including end
     * @returns slicedWordToken -  a new WordToken instance with the sliced text
     */
    slice(start: number, end: number): WordToken {
        // clamp values to bounds of the internal text length
        const clampedStart = Math.max(0, start);
        const clampedEnd = Math.min(end, this.numChars);
        let gIndex = 0;

        const slicedWordToken = new WordToken();

        this.#wordElementList.forEach((wordElement) => {
            const wordElStart = clampedStart - gIndex;
            const wordElEnd = clampedEnd - gIndex;
            const numChars = wordElement.numChars;

            if (wordElStart < numChars && wordElEnd > 0) {
                const slicedWordElementToken = wordElement.slice(wordElStart, wordElEnd);
                slicedWordToken.add(slicedWordElementToken);
            }
            gIndex += numChars;
        });

        return slicedWordToken;
    }

    /**
     * Returns the advance width from char <start> index up to but not including char <end>.
     *
     * @param start - is an index, must be an integer
     * @param end - is an index, must be an integer
     */
    partialAdvanceWidth(start: number, end: number): number {
        let charIndex = 0;
        return this.#wordElementList.reduce((gWidth, wordElement) => {
            const numChars = wordElement.numChars;
            const wordElStart = start - charIndex;
            const wordElEnd = end - charIndex;

            let width = 0;
            if (wordElStart < numChars && wordElEnd > 0) {
                width = wordElement.partialAdvanceWidth(wordElStart, wordElEnd);
            }

            // advance forward by all characters of the word element
            charIndex += numChars;
            return gWidth + width;
        }, 0);
    }

    /**
     * Clears the internal word element list
     * // TODO: not used?????
     */
    _reset() {
        this.#wordElementList = [];
    }

    /**
     * Reset word element metrics.
     */
    resetWordElementMetrics() {
        const newWordElements = [];
        this.#wordElementList.forEach((wordElement) => {
            const wordElementMetrics = new WordElementMetrics(wordElement.text, wordElement.font, wordElement.style, {
                wordElementToken: wordElement.wordElementToken,
                direction: wordElement.direction,
                fontUrl: wordElement.fontUrl,
                boundingBox: Rectangle.EMPTY,
                advanceWidth: 0,
            });
            newWordElements.push(wordElementMetrics);
        });
        this.#wordElementList = newWordElements;
    }
}
